/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Backdrop,
  CircularProgress,
  Snackbar
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { useMutation, useQuery } from "@tanstack/react-query";
import { FormikValues, useFormik } from "formik";
import * as Yup from "yup";

import { useStyles } from "./styles";
import StorageProvider from "../../../framework/src/StorageProvider";
import { createCustomFeature, fetchFeatureCategories } from "./queries";

interface NewCustomFeatureDialogProps {
  isOpen: boolean;
  handleClose: React.Dispatch<React.SetStateAction<boolean>>;
}

function NewCustomFeatureDialog({
  isOpen,
  handleClose
}: NewCustomFeatureDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles();

  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const fetchToken = async () => {
      const storedToken = await StorageProvider.get("authToken");
      if (storedToken) {
        setToken(storedToken);
      }
    };

    fetchToken();
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["featureCategories"],
    queryFn: () => fetchFeatureCategories(token!),
    enabled: !!token
  });

  const {
    isPending,
    mutate,
    failureCount,
    failureReason,
    isError: mutationIsError,
    isSuccess,
    reset,
  } = useMutation({
    mutationFn: async (form: FormikValues) => {
      if (!token) {
        return;
      }
      createCustomFeature(form, token);
    },
    onSuccess: () => {
      formik.resetForm();
    }
  });

  const formik = useFormik({
    initialValues: {
      featureName: "",
      featureCategoryId: null,
      featureFrequency: "Monthly",
      featureTag: "Individual",
      priceDetails: "One Time",
      defaultCost: ""
    },
    validationSchema: Yup.object({
      featureName: Yup.string().required("Required"),
      featureCategoryId: Yup.number()
        .typeError("Must be one of the options above")
        .required("Required"),
      featureFrequency: Yup.string().required("Required"),
      featureTag: Yup.string().required("Required"),
      priceDetails: Yup.string().required("Required"),
      defaultCost: Yup.number()
        .typeError("Must be a number")
        .required("Required")
        .test(
          "max-decimal-places",
          "Prices can have a maximum of 2 decimal places.",
          value => {
            if (value === null || value === undefined) {
              return true; // Allow null or undefined values if not required
            }
            // Convert the number to a string to apply regex
            const stringValue = String(value);
            // Regex to match a number with up to 2 decimal places
            // ^\\d+ matches one or more digits at the beginning
            // (\\.\\d{1,2})? matches an optional decimal point followed by 1 or 2 digits
            // $ ensures no other characters follow
            return /^(\d+(\.\d{1,2})?)$/.test(stringValue);
          }
        ),
    }),
    onSubmit: (values: FormikValues) => {
      mutate(values);
    }
  });

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleClose}
      scroll="body"
      aria-labelledby="responsive-dialog-title"
    >
      <Backdrop className={classes.backdrop} open={isPending}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={!isPending && mutationIsError}
        autoHideDuration={12000}
        onClose={() => reset()}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" elevation={6} variant="filled">
          <Typography variant="h3">Could not save your feature!</Typography>
          <Typography>{`${failureCount} attempts made`}</Typography>
          <Typography>{`Details: ${failureReason}`}</Typography>
        </Alert>
      </Snackbar>
      <Snackbar
        open={!isPending && isSuccess}
        autoHideDuration={12000}
        onClose={() => reset()}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          <Typography variant="h3">
            New feature created successfully!
          </Typography>
          <Button
            color="inherit"
            size="medium"
            variant="text"
            onClick={_ => window.location.replace(window.location.href)}
          >
            Reload
          </Button>
        </Alert>
      </Snackbar>
      <DialogTitle disableTypography>
        <Typography className={classes.dialogTitle}>Add Feature</Typography>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Feature Name */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
              >
                <TextField
                  label="Feature Name"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  {...formik.getFieldProps("featureName")}
                  error={
                    formik.touched.featureName &&
                    Boolean(formik.errors.featureName)
                  }
                  helperText={
                    formik.touched.featureName && formik.errors.featureName
                  }
                />
              </FormControl>
            </Grid>
            {/* Feature Category */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
                error={
                  formik.touched.featureCategoryId &&
                  Boolean(formik.errors.featureCategoryId)
                }
              >
                <InputLabel id="feature-category-select-label">
                  Feature Category
                </InputLabel>
                {isLoading && <LinearProgress />}
                {!isLoading && (
                  <Select
                    labelId="feature-category-select-label"
                    id="feature-category-select"
                    {...formik.getFieldProps("featureCategoryId")}
                    label="Feature Category"
                    error={isError}
                  >
                    {!isError &&
                      data.data.map(category => {
                        return (
                          <MenuItem value={category.attributes.id}>
                            {category.attributes.category_name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                )}
                <FormHelperText error>
                  {(formik.touched.featureCategoryId &&
                    formik.errors.featureCategoryId) ||
                    (isError && "Failed to fetch your categories")}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* Feature Frequency */}
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
                error={
                  formik.touched.featureFrequency &&
                  Boolean(formik.errors.featureFrequency)
                }
              >
                <InputLabel id="feature-frequency-select-label">
                  Feature Frequency
                </InputLabel>
                <Select
                  labelId="feature-frequency-select-label"
                  id="feature-frequency-select"
                  {...formik.getFieldProps("featureFrequency")}
                  value={formik.values.featureFrequency}
                  label="Feature Frequency"
                >
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Quarterly">Quarterly</MenuItem>
                  <MenuItem value="Annually">Annually</MenuItem>
                </Select>
                <FormHelperText error>
                  {formik.touched.featureFrequency &&
                    formik.errors.featureFrequency}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* Feature Tag */}
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
                error={
                  formik.touched.featureTag && Boolean(formik.errors.featureTag)
                }
              >
                <InputLabel id="feature-tag-select-label">
                  Feature Tag
                </InputLabel>
                <Select
                  labelId="feature-tag-select-label"
                  id="feature-tag-select"
                  {...formik.getFieldProps("featureTag")}
                  value={formik.values.featureTag}
                  label="Feature Tag"
                >
                  <MenuItem value="Individual">Individual</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="Both">Both</MenuItem>
                </Select>
                <FormHelperText error>
                  {formik.touched.featureTag && formik.errors.featureTag}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* Price Details */}
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
                error={
                  formik.touched.priceDetails &&
                  Boolean(formik.errors.priceDetails)
                }
              >
                <InputLabel id="price-details-select-label">
                  Price Details
                </InputLabel>
                <Select
                  labelId="price-details-select-label"
                  id="price-details-select"
                  {...formik.getFieldProps("priceDetails")}
                  value={formik.values.priceDetails}
                  label="Price Details"
                >
                  <MenuItem value="One Time">One Time</MenuItem>
                  <MenuItem value="Recurring">Recurring</MenuItem>
                </Select>
                <FormHelperText error>
                  {formik.touched.priceDetails && formik.errors.priceDetails}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* Default Feature Cost */}
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
              >
                <TextField
                  id="outlined-number"
                  label="Default Feature Cost ($)"
                  {...formik.getFieldProps("defaultCost")}
                  error={
                    formik.touched.defaultCost &&
                    Boolean(formik.errors.defaultCost)
                  }
                  helperText={
                    formik.touched.defaultCost && formik.errors.defaultCost
                  }
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => handleClose(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button variant="contained" type="submit" color="primary">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default NewCustomFeatureDialog;
